import { Injectable, ComponentRef, Type, ApplicationRef, Injector, EmbeddedViewRef, ComponentFactoryResolver } from '@angular/core';
import { Subject } from 'rxjs';

export interface ModalConfig {
  component: Type<any>;
  data?: any;
  width?: string;
  disableClose?: boolean;
}

export interface ModalRef<T = any> {
  instance: T;
  close: (result?: any) => void;
  afterClosed: () => Subject<any>;
}

@Injectable({ providedIn: 'root' })
export class DynamicModalService {
  private contentComponentRef: ComponentRef<any> | null = null;
  private backdropElement: HTMLElement | null = null;
  private modalElement: HTMLElement | null = null;
  private closeSubject: Subject<any> | null = null;
  private escHandler: ((event: KeyboardEvent) => void) | null = null;
  private isClosing: boolean = false;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  /**
   * Opens a modal with the specified component
   * @param config Modal configuration containing component and data
   * @returns ModalRef with instance, close method, and afterClosed observable
   */
  open<T>(config: ModalConfig): ModalRef<T> {
    // Close any existing modal first
    if (this.backdropElement) {
      this.closeModal();
    }

    this.isClosing = false;
    this.closeSubject = new Subject<any>();
    const currentCloseSubject = this.closeSubject;

    // Create backdrop - z-index lower than loader (100) so loader appears on top
    this.backdropElement = document.createElement('div');
    this.backdropElement.className = 'dynamic-modal-backdrop';
    this.backdropElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 99;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Create modal container
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'dynamic-modal-container';
    this.modalElement.style.cssText = `
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      max-height: 90vh;
      max-width: 90vw;
      width: ${config.width || '600px'};
      overflow: auto;
      z-index: 99;
      animation: modalFadeIn 0.2s ease-out;
    `;

    // Add animation styles if not already added
    if (!document.getElementById('dynamic-modal-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'dynamic-modal-styles';
      styleElement.textContent = `
        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `;
      document.head.appendChild(styleElement);
    }

    // Handle backdrop click
    if (!config.disableClose) {
      this.backdropElement.addEventListener('click', (event) => {
        if (event.target === this.backdropElement) {
          this.close(null);
        }
      });
    }

    // Prevent modal container clicks from closing
    this.modalElement.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    // Append elements to DOM
    this.backdropElement.appendChild(this.modalElement);
    document.body.appendChild(this.backdropElement);
    document.body.style.overflow = 'hidden';

    // Create the component dynamically
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(config.component);
    this.contentComponentRef = componentFactory.create(this.injector);

    // Pass data to component if provided
    if (config.data) {
      Object.keys(config.data).forEach(key => {
        this.contentComponentRef.instance[key] = config.data[key];
      });
    }

    // Provide close method to the component - bind to this service's close method
    const closeMethod = (result?: any) => {
      this.close(result);
    };
    this.contentComponentRef.instance['modalClose'] = closeMethod;

    // Attach component to Angular's component tree
    this.appRef.attachView(this.contentComponentRef.hostView);

    // Get DOM element from component
    const domElement = (this.contentComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    this.modalElement.appendChild(domElement);

    // Handle ESC key
    if (!config.disableClose) {
      this.escHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.close(null);
        }
      };
      document.addEventListener('keydown', this.escHandler);
    }

    return {
      instance: this.contentComponentRef.instance as T,
      close: closeMethod,
      afterClosed: () => currentCloseSubject
    };
  }

  /**
   * Closes the current modal with an optional result
   */
  close(result?: any): void {
    if (this.isClosing) return;
    this.isClosing = true;

    if (this.closeSubject && !this.closeSubject.closed) {
      this.closeSubject.next(result);
      this.closeSubject.complete();
    }
    this.closeModal();
  }

  private closeModal(): void {
    // Remove ESC handler
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }

    // Detach and destroy content component
    if (this.contentComponentRef) {
      this.appRef.detachView(this.contentComponentRef.hostView);
      this.contentComponentRef.destroy();
      this.contentComponentRef = null;
    }

    // Remove backdrop from DOM
    if (this.backdropElement && this.backdropElement.parentNode) {
      this.backdropElement.parentNode.removeChild(this.backdropElement);
      this.backdropElement = null;
    }

    this.modalElement = null;
    this.closeSubject = null;
    document.body.style.overflow = '';
    this.isClosing = false;
  }
}

